import { Box, Typography, useTheme } from "@material-ui/core";
import { useFieldValue } from "formik";
import { Maybe } from "graphql/jsutils/Maybe";
import { useTranslation } from "react-i18next";
import * as Yup from "yup";
import LAYearDatePicker from "../components/LAYearDatePicker";
import useStyles from "../containers/data/forms/forms-styles";
import { EMPTY_VALUE_PLACEHOLDER } from "../models/common/data";
import { FlexFieldController } from "../models/flexible/flex-field-controller";
import { isEmptyValue } from "../utils/is-empty-value";
import { GeneralFieldController } from "./general-field-controller";
import { ControlState } from "../models/flexible/control-state";
import { AttributeIdentifiers } from "@lyfegen-platform/graphql-schema/models";

const maxYear = new Date().getFullYear();

function RenderYearSelector(props: { controller: FlexFieldController; path: string }) {
	const { t } = useTranslation();
	const styles = useStyles();
	const { palette } = useTheme();
	const [value] = useFieldValue(props.path);

	if (!props.controller.parent.context.isEditable) {
		return (
			<Box className={styles.colShort} pr={5} pl={1.5} paddingY={2.5}>
				<Typography variant="caption" color="textSecondary">
					{t(props.controller.schema.attribute.shortLabelLocalizationKey)}
				</Typography>
				<Typography style={{ color: palette.text.disabled }}>{isEmptyValue(value) ? EMPTY_VALUE_PLACEHOLDER : value}</Typography>
			</Box>
		);
	}

	return (
		<Box className={styles.colShort} pr={5} paddingY={2.5}>
			<LAYearDatePicker name={props.path} label={t("yob")} maxYear={maxYear} />
		</Box>
	);
}

export class YearOfBirthFieldController extends GeneralFieldController {
	customRenderContent(path: string): React.ReactElement {
		return <RenderYearSelector controller={this} path={path} />;
	}

	override mapInitialValue(value: unknown): unknown {
		return (
			value ||
			this.parent.context.startingPoint?.data[AttributeIdentifiers.YearOfBirth as any] ||
			""
		);
	}

	override useControlState(): ControlState {
		return this.parent.context.noPatients && this.parent.context.noPatients > 0 ? "hidden" : "enabled";
	}

	override createValidationRule(_initialValues: undefined, translate: (key: string) => string): Yup.AnySchema {
		return Yup.number()
			.nullable()
			.test("is-previous", translate("cannot-be-future"), (year?: any) => !year || year <= maxYear);
	}

	override mapFormValue(value: Maybe<number>) {
		return value || null;
	}
}
